import { TextField, Typography } from "@mui/material";
import { Autocomplete } from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { isArray, isEmpty, isNumber } from "lodash";
import React, { useState } from "react";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { Category } from "@converge-collective/common/models/Category";
import { SubCategoryWithCategory } from "@converge-collective/common/models/SubCategory";

type Props = {
  multiple?: boolean;
  required?: boolean;
  onSetSubCategories: (subCats: SubCategoryWithCategory[]) => void;
  filterZeroPointCats?: boolean;
  helperText?: string;
};

export default function AutocompleteCategory({
  onSetSubCategories,
  multiple = false,
  required = true,
  filterZeroPointCats = false,
  helperText = "",
}: Props): React.ReactElement {
  const firestore = useFirestore();
  const [selectedSubCategory, setSelectedSubCategory] = useState<
    SubCategoryWithCategory[] | null
  >(null);
  const [validCategory, setValidCategory] = useState(true);

  const validateCategory = (value = selectedSubCategory) => {
    setValidCategory(!isEmpty(value));
  };

  const catRef = firestore.collection("categories");
  const { data: categories } = useFirestoreCollectionData<Category>(catRef, {
    idField: "id",
  });

  const loading = !categories;
  const subCatWithCats =
    categories &&
    (categories
      .flatMap((category) =>
        Object.entries(category.subCategories)
          .map(([id, subCat]) => ({ category, subCategory: { ...subCat, id } }))
          .filter(
            // filter out the categories with 0 points unless
            // filterZeroPointCats == false
            ({ subCategory }) =>
              !filterZeroPointCats || (subCategory.points || 0) > 0
          )
      )
      .sort((a, b) =>
        a.subCategory.subCategory.localeCompare(b.subCategory.subCategory)
      ) as SubCategoryWithCategory[]);

  const pointsForTag = ({ subCategory }: SubCategoryWithCategory) =>
    (isNumber(subCategory.points) ? subCategory.points : 0) * 10;

  return (
    <Autocomplete
      openOnFocus
      autoComplete
      autoSelect
      multiple={multiple}
      autoHighlight
      loading={loading}
      options={subCatWithCats}
      getOptionLabel={(subCat: SubCategoryWithCategory) =>
        subCat.subCategory.subCategory || "None"
      }
      isOptionEqualToValue={(option, value) =>
        option.subCategory.id === value.subCategory.id
      }
      onChange={(_, newValue) => {
        console.log("set category", { newValue });
        if (newValue) {
          const subCats = isArray(newValue) ? newValue : [newValue];
          validateCategory(subCats);
          setSelectedSubCategory(subCats);
          onSetSubCategories(subCats);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={multiple ? "Categories" : "Category"}
          helperText={helperText}
          fullWidth
          error={required && !validCategory}
        />
      )}
      renderOption={(
        props,
        option: SubCategoryWithCategory,
        { inputValue }
      ) => {
        const matches = match(option.subCategory.subCategory, inputValue);
        const parts = parse(option.subCategory.subCategory, matches);

        return (
          <li
            {...props}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              {parts.map((part, index: number) => (
                <span
                  key={index}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}
            </div>
            <div style={{ color: "gray" }}>
              <Typography variant="overline">{pointsForTag(option)}</Typography>
            </div>
          </li>
        );
      }}
    />
  );
}
